import { css } from "@emotion/css";
import { ChevronLeftIcon, ChevronRightIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { format } from "date-fns";
import type React from "react";
import type { ButtonProps as DayPickerButtonProps } from "react-day-picker";
import { DayPicker, DayFlag, SelectionState, UI, useDayPicker } from "react-day-picker";
import { Button } from "../../Button";
interface DatePickerCalendarProps {
    selected: Date | undefined;
    onSelect: (date: Date) => void;
    month: Date | undefined;
    onMonthChange: (date: Date | undefined) => void;
    startMonth: Date;
    endMonth: Date;
    disablePastDates?: boolean;
}
export function DatePickerCalendar({ selected, onSelect, month, onMonthChange, startMonth, endMonth, disablePastDates }: DatePickerCalendarProps) {
    return (<DayPicker selected={selected} onSelect={onSelect} month={month} onMonthChange={onMonthChange} startMonth={startMonth} endMonth={endMonth} disabled={disablePastDates ? { before: new Date() } : undefined} classNames={calendarStyles} required={true} mode="single" autoFocus={true} role={"dialog"} captionLayout={"dropdown"} navLayout={"after"} showOutsideDays={true} fixedWeeks={true} components={{
            PreviousMonthButton: PreviousMonthButton,
            NextMonthButton: NextMonthButton,
        }} formatters={{
            formatMonthDropdown: (month) => format(month, "MMM"),
        }}/>);
}
const calendarStyles = {
    [UI.Root]: css({
        padding: `${space[12]} ${space[12]} ${space[8]}`,
        backgroundColor: themeTokens.color.background.quartary.default,
    }),
    [UI.Month]: css({
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gridTemplateRows: "auto auto",
        gridTemplateAreas: `
            "caption nav"
            "grid grid"
        `,
        gridRowGap: space[4],
    }),
    [UI.MonthCaption]: css({
        gridArea: "caption",
        display: "flex",
        alignItems: "center",
    }),
    [UI.Nav]: css({
        gridArea: "nav",
        display: "flex",
        gap: space[8],
        width: "fit-content",
    }),
    [UI.CaptionLabel]: css({
        display: "none",
    }),
    [UI.Dropdowns]: css({
        display: "flex",
        gap: space[12],
    }),
    [UI.Dropdown]: css({
        color: themeTokens.color.text.primary,
        font: text.heading.small,
        height: "1.75rem",
        backgroundColor: "transparent",
        border: "2px solid transparent",
        borderRadius: borderRadius.medium,
        cursor: "pointer",
        "&:focus-visible": {
            outline: "none",
            borderColor: themeTokens.color.border.selected,
        },
    }),
    [UI.MonthGrid]: css({
        gridArea: "grid",
        tableLayout: "fixed",
        width: "calc(2.25rem * 7)", // 7 days × 36px each
        borderCollapse: "collapse",
    }),
    [UI.Weekday]: css({
        height: "2.25rem",
        width: "2.25rem",
        padding: 0,
        font: text.body.bold.xSmall,
        color: themeTokens.color.text.secondary,
    }),
    [UI.Day]: css({
        font: text.body.regular.xSmall,
        height: "2.25rem",
        width: "2.25rem",
        textAlign: "center",
        verticalAlign: "middle",
        padding: 0,
    }),
    [UI.DayButton]: css({
        all: "unset",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: "2.125rem",
        width: "2.125rem",
        cursor: "pointer",
        borderRadius: "50%",
        boxSizing: "border-box",
        "&:hover": {
            backgroundColor: themeTokens.color.button.background.quiet.hover,
        },
        "&:focus-visible": {
            outline: `2px solid ${themeTokens.color.border.selected}`,
            outlineOffset: space[1],
            backgroundColor: themeTokens.color.button.background.quiet.hover,
        },
    }),
    [DayFlag.outside]: css({
        color: themeTokens.color.text.tertiary,
    }),
    [DayFlag.disabled]: css({
        color: themeTokens.color.text.tertiary,
        "& button": {
            cursor: "not-allowed",
            "&:hover": {
                backgroundColor: "transparent",
            },
        },
    }),
    [DayFlag.today]: css({
        "& button": {
            border: `1px solid ${themeTokens.color.border.selected}`,
            "&:focus-visible": {
                backgroundColor: themeTokens.color.background.quartary.default,
            },
        },
    }),
    [SelectionState.selected]: css({
        "& button": {
            color: themeTokens.color.text.inverse.primary,
            font: text.body.bold.xSmall,
            backgroundColor: themeTokens.color.background.selected,
            "&:hover": {
                color: themeTokens.color.text.primary,
            },
            "&:focus-visible": {
                backgroundColor: themeTokens.color.background.selected,
                color: themeTokens.color.text.inverse.primary,
            },
        },
    }),
};
function PreviousMonthButton({ onClick }: DayPickerButtonProps) {
    const { previousMonth } = useDayPicker();
    const handleClick = (e: React.MouseEvent) => {
        if (isButtonMouseEvent(e)) {
            onClick?.(e);
        }
    };
    return <Button importance="neutral" icon={<ChevronLeftIcon size={20}/>} onClick={handleClick} disabled={previousMonth === undefined} accessibleName="Previous month"/>;
}
function NextMonthButton({ onClick }: DayPickerButtonProps) {
    const { nextMonth } = useDayPicker();
    const handleClick = (e: React.MouseEvent) => {
        if (isButtonMouseEvent(e)) {
            onClick?.(e);
        }
    };
    return <Button importance="neutral" icon={<ChevronRightIcon size={20}/>} onClick={handleClick} disabled={nextMonth === undefined} accessibleName="Next month"/>;
}
function isButtonMouseEvent(e: React.MouseEvent): e is React.MouseEvent<HTMLButtonElement> {
    return e.currentTarget instanceof HTMLButtonElement;
}
